import {
    forwardRef,
    type ChangeEvent,
    type InputHTMLAttributes,
    type Ref,
    type TextareaHTMLAttributes,
} from "react";
import { FormField } from "./FormField";
import { TextInput } from "./TextInput";
import { Textarea } from "./Textarea";

// TextField — a labelled text control. The common case, in one component.
//
// WHY THIS EXISTS. `FormField` + `TextInput` is the composition the DS has
// always offered, and ~29 call sites assemble it by hand. Each one repeats the
// same three things: wrap the control, pass `error` twice (a STRING to the field
// for the message, a BOOLEAN to the control for the red border), and unwrap
// `e.target.value` before it reaches state. Homebase got tired enough of it to
// write its own wrapper; that wrapper is the thing that belonged here.
//
//     <FormField label="Email" helper="We'll send the invite here." error={err}>
//         <TextInput value={v} error={!!err} onChange={(e) => set(e.target.value)} />
//     </FormField>
//
//     <TextField label="Email" hint="We'll send the invite here." error={err}
//                value={v} onChange={set} />
//
// `FormField` stays public and is still the right answer for a labelled control
// this does not wrap — a Select, a PhoneInput, a CheckList. This covers text.
//
// `multiline` rather than a second component: the two differ only in which
// control they render, and a TextareaField would duplicate every prop to say so.

type SharedProps = {
    value: string;
    /** The VALUE, not the event — unwrapped here so no call site has to. */
    onChange: (value: string) => void;
    label?: string;
    /** Muted helper line under the control. Hidden while an error shows. */
    hint?: string;
    /** The message. Its presence also puts the control in its error state, so
     *  there is no way to show one without the other. */
    error?: string;
    optional?: boolean;
    /** Visually hide the label, keeping it for screen readers. */
    labelHidden?: boolean;
};

export type TextFieldProps = SharedProps &
    ({ multiline?: false } & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">
        | { multiline: true } & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value">);

/** Ref reaches the CONTROL, not the wrapper — a caller asking for a ref wants to
 *  focus or measure the input, never the label stack around it. */
export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(
    function TextField(
        { value, onChange, label, hint, error, optional, labelHidden, ...rest },
        ref,
    ) {
    const { multiline, ...controlProps } = rest as { multiline?: boolean } & Record<string, unknown>;
    return (
        <FormField
            label={label}
            labelHidden={labelHidden}
            helper={hint}
            error={error}
            optional={optional}
        >
            {multiline ? (
                <Textarea
                    ref={ref as Ref<HTMLTextAreaElement>}
                    {...(controlProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                    value={value}
                    error={Boolean(error)}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                />
            ) : (
                <TextInput
                    ref={ref as Ref<HTMLInputElement>}
                    {...(controlProps as InputHTMLAttributes<HTMLInputElement>)}
                    value={value}
                    error={Boolean(error)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                />
            )}
        </FormField>
    );
},
);
